'use client';

import React, { useCallback, useRef } from 'react';

import styles from './SectionTabs.module.scss';

export type SectionTabItem = {
  id: string;
  label: string;
  disabled?: boolean;
};

export type SectionTabsProps = {
  sections: SectionTabItem[];
  activeId: string;
  onSectionChange: (id: string) => void;
  className?: string;
  sectionClassName?: string;
  orientation?: 'horizontal' | 'vertical';
  'aria-label'?: string;
};

export function SectionTabs({
  sections,
  activeId,
  onSectionChange,
  className,
  sectionClassName,
  orientation = 'horizontal',
  'aria-label': ariaLabel = 'Section tabs',
}: SectionTabsProps) {
  const sectionRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, currentIndex: number) => {
      let newIndex = currentIndex;

      if (orientation === 'horizontal') {
        if (event.key === 'ArrowLeft') {
          newIndex = Math.max(0, currentIndex - 1);
        } else if (event.key === 'ArrowRight') {
          newIndex = Math.min(sections.length - 1, currentIndex + 1);
        }
      } else {
        if (event.key === 'ArrowUp') {
          newIndex = Math.max(0, currentIndex - 1);
        } else if (event.key === 'ArrowDown') {
          newIndex = Math.min(sections.length - 1, currentIndex + 1);
        }
      }

      if (event.key === 'Home') {
        newIndex = 0;
      } else if (event.key === 'End') {
        newIndex = sections.length - 1;
      }

      if (newIndex !== currentIndex) {
        event.preventDefault();
        const newTab = sections[newIndex];
        if (newTab) {
          sectionRefs.current.get(newTab.id)?.focus();
        }
      }
    },
    [orientation, sections],
  );

  if (sections.length === 0) {
    return null;
  }

  return (
    <div className={`${styles.sectionTabs} ${className || ''}`}>
      <div
        role="tablist"
        aria-label={ariaLabel}
        className={`${styles.tabsContainer} ${
          orientation === 'vertical' ? styles.tabsContainerVertical : ''
        }`}
      >
        {sections.map((section, index) => {
          const isActive = activeId === section.id;
          const isDisabled = Boolean(section.disabled);

          return (
            <button
              key={section.id}
              type="button"
              aria-label={section.label}
              aria-selected={isActive}
              aria-disabled={isDisabled}
              role="tab"
              id={`section-tab-${index}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => {
                if (!isDisabled) {
                  onSectionChange(section.id);
                }
              }}
              onKeyDown={(event) => {
                if (isDisabled) {
                  event.preventDefault();
                  return;
                }
                handleKeyDown(event, index);
              }}
              ref={(element) => {
                if (element) {
                  sectionRefs.current.set(section.id, element);
                } else {
                  sectionRefs.current.delete(section.id);
                }
              }}
              className={`${styles.sectionButton} ${sectionClassName || ''}`}
              data-variant={isActive ? 'primary' : undefined}
              disabled={isDisabled}
            >
              {section.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
